import { getSeonSession, initSeon, SEON_FEATURE } from "@configs/seon";
import { SDKInitOptions, SDKOptions } from "@seontechnologies/seon-javascript-sdk";
import logger from "@utils/logger";
import { useCallback, useEffect, useState } from "react";

interface SeonSessionConfigOptions {
  /**
   * Automatically initialize seon when the component mounts
   */
  autoInit?: boolean;
  /**
   * Initialize seon with the given config
   */
  initConfig?: SDKInitOptions;
  /**
   * Automatically get session when the component mounts
   * Session will be set to seonSession
   */
  autoGetSession?: boolean;
}

/**
 * Seon session hook to init and get session for a specific feature
 * A wrapper for seon javascript sdk, customized to work with React
 * - Use options.autoInit to automatically initialize seon when the component mounts
 * - Use getSession to get the session before sending payload to backend
 *
 * @returns
 * - isFetching: boolean - whether the session is being fetched
 * - getSession: function to get the session - will return a promise that resolves to the session
 * - seonSession: string | undefined - the session if it has been fetched
 */
export const useSeonSession = (feature: SEON_FEATURE, options?: SeonSessionConfigOptions) => {
  const [isFetching, setIsFetching] = useState(false);
  const [seonSession, setSeonSession] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (options?.autoInit) {
      initSeon(options?.initConfig);
      logger._console.log(`[Seon] Initialized feature: ${feature}`);
    }
  }, [options?.autoInit, options?.initConfig, feature]);

  const getSession = useCallback(
    async (config?: SDKOptions) => {
      logger._console.log(`[Seon] Getting session for feature: ${feature}`);
      setIsFetching(true);
      const session = await getSeonSession(config);
      setIsFetching(false);
      setSeonSession(session);
      return session;
    },
    [feature],
  );

  useEffect(() => {
    if (options?.autoGetSession) {
      getSession().then((session) => {
        setSeonSession(session);
      });
    }
  }, [options?.autoGetSession, getSession]);

  return {
    isFetching,
    getSession,
    seonSession,
  };
};
